import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { Link, useNavigate } from "react-router-dom";
import { splitDescription } from "../lib/catalogSanitize.js";
import ZoomImage from "./ZoomImage.jsx";

// Build the pre-filled Sales message for a quote request. Only includes fields
// that are present, so the reference block never shows blank labels.
function buildQuoteMessage(item) {
  const hasPack = item.packQty != null && item.packQty !== "" && item.packUnit;
  const lines = [
    "I'd like to request a quote for the following item:",
    "",
    `Product: ${item.name}`,
    `SKU: ${item.sku}`,
  ];
  if (item.category) lines.push(`Category: ${item.category}`);
  if (hasPack) lines.push(`Pack: ${item.packQty} per ${item.packUnit}`);
  if (item.sizes) lines.push(`Sizes: ${item.sizes}`);
  lines.push("", "Quantity needed:", "Additional details:");
  return lines.join("\n");
}

function DescriptionBlock({ description }) {
  // Description paragraphs are stored ";"-joined (legacy data may be "\n"-joined
  // or an array of lines); splitDescription normalizes all three to paragraphs.
  const lines = splitDescription(description);
  if (!lines.length) return null;
  // Group adjacent bullet lines (starting with •) into a single <ul>; render
  // the rest as <p>. Strip the leading bullet glyph from list items since the
  // <ul> provides one.
  const out = [];
  let bulletBuf = [];
  const flushBullets = () => {
    if (!bulletBuf.length) return;
    out.push(
      <ul key={`u-${out.length}`} className="mb-3 ps-3">
        {bulletBuf.map((b, i) => (
          <li key={i} className="mb-1">{b}</li>
        ))}
      </ul>
    );
    bulletBuf = [];
  };
  for (const raw of lines) {
    if (!raw || !raw.trim()) continue;
    if (raw.startsWith("•")) {
      bulletBuf.push(raw.replace(/^•\s*/, ""));
    } else {
      flushBullets();
      out.push(
        <p key={`p-${out.length}`} className="text-secondary mb-3" style={{ lineHeight: 1.7 }}>
          {raw}
        </p>
      );
    }
  }
  flushBullets();
  return <div className="pmp-catalog-detail-desc">{out}</div>;
}

function PartLink({ sku, name }) {
  return (
    <li className="mb-2">
      <Link to={`/catalog/${encodeURIComponent(sku)}`} className="text-decoration-none">
        {name}
      </Link>
      <div className="small text-secondary font-monospace">{sku}</div>
    </li>
  );
}

export default function CatalogItemDetail({ item }) {
  const navigate = useNavigate();
  const imageUrl = item.imageUrl ?? item.image;
  const placeholderLabel = item.imageAlt ?? item.name;
  const materials = Array.isArray(item.materials) ? item.materials : [];
  const features = Array.isArray(item.features) ? item.features : [];
  const partsExpanded = Array.isArray(item.partsExpanded) ? item.partsExpanded : [];
  const usedInKits = Array.isArray(item.usedInKits) ? item.usedInKits : [];
  const declaredParts = Array.isArray(item.parts) ? item.parts : [];
  const expandedSkus = new Set(partsExpanded.map((p) => p.sku));
  const missingParts = declaredParts.filter((sku) => !expandedSkus.has(sku));
  const hasPack = item.packQty != null && item.packQty !== "" && item.packUnit;

  return (
    <div className="pmp-catalog-detail">
      <Row className="g-4 g-lg-5 align-items-start">
        <Col xs={12} lg={5}>
          <div
            className="overflow-hidden pmp-catalog-detail-media"
            style={{
              border: "1px solid rgba(0,0,0,0.08)",
              boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
              borderRadius: "1.25rem",
            }}
          >
            {imageUrl ? (
              <ZoomImage src={imageUrl} alt={item.imageAlt ?? item.name} />
            ) : (
              <div className="d-flex align-items-center justify-content-center text-secondary small px-3 text-center w-100 h-100 pmp-placeholder-img">
                {placeholderLabel}
              </div>
            )}
          </div>
        </Col>

        <Col xs={12} lg={7}>
          <p className="pmp-kicker mb-2">{item.category}</p>
          <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
            <h1 className="h2 fw-bold mb-0">{item.name}</h1>
            {item.subCategory ? (
              <Badge bg="secondary" pill className="fw-normal align-self-center">
                {item.subCategory}
              </Badge>
            ) : null}
          </div>

          <DescriptionBlock description={item.description} />

          {features.length ? (
            <div className="mb-3">
              <h6 className="fw-semibold mb-2">Features</h6>
              <ul className="mb-0 ps-3">
                {features.map((f, i) => (
                  <li key={i} className="mb-1 text-secondary" style={{ lineHeight: 1.7 }}>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <dl
            className="row mb-0 small mt-2"
            style={{
              borderTop: "1px solid rgba(0,0,0,0.08)",
              paddingTop: "1rem",
            }}
          >
            <dt className="col-sm-4 col-lg-3 text-secondary fw-normal">
              SKU
            </dt>
            <dd className="col-sm-8 col-lg-9 font-monospace mb-3">
              {item.sku}
            </dd>
            {hasPack ? (
              <>
                <dt className="col-sm-4 col-lg-3 text-secondary fw-normal">
                  Pack
                </dt>
                <dd className="col-sm-8 col-lg-9 mb-3">
                  {item.packQty} per {item.packUnit}
                </dd>
              </>
            ) : null}
            {item.sizes ? (
              <>
                <dt className="col-sm-4 col-lg-3 text-secondary fw-normal">
                  Sizes
                </dt>
                <dd className="col-sm-8 col-lg-9 mb-3">{item.sizes}</dd>
              </>
            ) : null}
          </dl>

          {materials.length ? (
            <div className="mt-1">
              <p className="small text-secondary mb-2 fw-semibold">
                Materials
              </p>
              <div className="d-flex flex-wrap gap-2">
                {materials.map((m, i) => (
                  <Badge
                    key={`${i}-${m}`}
                    pill
                    bg="primary"
                    className="fw-normal px-3 py-2"
                  >
                    {m}
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}

          {partsExpanded.length || missingParts.length ? (
            <div className="mt-4">
              <p className="small text-secondary mb-2 fw-semibold">
                Kit contents
              </p>
              <ul className="list-unstyled mb-0">
                {partsExpanded.map((p) => (
                  <PartLink key={p.sku} sku={p.sku} name={p.name} />
                ))}
                {missingParts.map((sku) => (
                  <li key={sku} className="mb-2 text-secondary">
                    <span className="font-monospace">{sku}</span>
                    <div className="small">(part not in catalog)</div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {usedInKits.length ? (
            <div className="mt-4">
              <p className="small text-secondary mb-2 fw-semibold">
                Also sold in
              </p>
              <ul className="list-unstyled mb-0">
                {usedInKits.map((k) => (
                  <PartLink key={k.sku} sku={k.sku} name={k.name} />
                ))}
              </ul>
            </div>
          ) : null}

          <div className="mt-4">
            <Button
              type="button"
              variant="success"
              size="lg"
              style={{ borderRadius: 4, fontWeight: 600 }}
              onClick={() =>
                navigate("/contacts", {
                  state: { quote: { message: buildQuoteMessage(item), formType: "sales" } },
                })
              }
            >
              Request for Quote
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  );
}
